import { useCallback } from "react";

import { type LinkProps, type NavigateOptions, type To } from "@cinnamon/design-system/hooks";

import { TENANT_ID_PARAM } from "@/constants/config";
import logger from "@/helper/logger";
import { useAppSelector } from "@/redux/hook";
import { userState } from "@/redux/store";

/**
 * Options for navigation with tenant awareness
 */
export interface TenantAwareNavigateOptions extends NavigateOptions {
  /** Skip tenant ID injection for this specific navigation */
  skipTenantId?: boolean;
}

export interface UseTenantAwareNavigationLinkPropsConfig {
  excludedRoutes: string[];
  debug: boolean;
}

/**
 * Default routes that should not have tenantId injected
 */
const DEFAULT_EXCLUDED_ROUTES = [
  "/login",
  "/logout",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/public",
  "/404",
  "/error",
  "/maintenance",
];

/**
 * Check if a pathname should be excluded from tenant injection
 *
 * @param pathname - The pathname to check
 * @param excludedRoutes - List of excluded route prefixes
 * @returns True if the route should be excluded
 */
const shouldExcludeRoute = (pathname: string, excludedRoutes: string[]): boolean => {
  return excludedRoutes.some((route) => pathname.startsWith(route));
};

/**
 * Inject tenantId into a string URL
 *
 * @param url - The string URL to inject tenant into
 * @param tenantId - The tenant ID to inject
 * @param excludedRoutes - Routes that should not have tenant injected
 * @returns The URL with tenant ID injected (if applicable)
 */
const injectTenantIdIntoStringUrl = (url: string, tenantId: string, excludedRoutes: string[]): string => {
  const [pathname, search = ""] = url.split("?");

  // Check if route should be excluded
  if (shouldExcludeRoute(pathname, excludedRoutes)) {
    return url;
  }

  // Parse existing search params
  const params = new URLSearchParams(search);

  // Only inject if not already present
  const shouldSetTenantId = !params.has(TENANT_ID_PARAM) || params.get(TENANT_ID_PARAM) !== tenantId;
  if (shouldSetTenantId) {
    params.set(TENANT_ID_PARAM, tenantId);
  }

  const newSearch = params.toString();
  return newSearch ? `${pathname}?${newSearch}` : pathname;
};

/**
 * Inject tenantId into an object URL
 *
 * @param to - The object URL to inject tenant into
 * @param to.pathname - The pathname part of the URL
 * @param to.search - The search params part of the URL
 * @param tenantId - The tenant ID to inject
 * @param excludedRoutes - Routes that should not have tenant injected
 * @returns The URL with tenant ID injected (if applicable)
 */
const injectTenantIdIntoObjectUrl = (
  to: { pathname?: string; search?: string },
  tenantId: string,
  excludedRoutes: string[]
): { pathname: string; search: string } => {
  const pathname = to.pathname || "";
  const search = to.search || "";

  // Check if route should be excluded
  if (shouldExcludeRoute(pathname, excludedRoutes)) {
    return { pathname, search };
  }

  // Parse existing search params
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);

  // Only inject if not already present or if different
  const shouldSetTenantId = params.get(TENANT_ID_PARAM) !== tenantId;
  if (shouldSetTenantId) {
    params.set(TENANT_ID_PARAM, tenantId);
  }

  const newSearch = params.toString();
  return {
    pathname,
    search: newSearch ? `?${newSearch}` : "",
  };
};

/**
 * Inject tenantId into a URL if not already present
 *
 * @param to - The destination URL (string or object)
 * @param tenantId - The tenant ID to inject
 * @param excludedRoutes - Routes that should not have tenant injected
 * @returns The URL with tenant ID injected (if applicable)
 */
const injectTenantIdIntoUrl = (to: To, tenantId: string | null | undefined, excludedRoutes: string[]): To => {
  if (!tenantId) {
    if (typeof to === "string") {
      return to;
    }
    return {
      pathname: to.pathname || "",
      search: to.search || "",
    };
  }

  // Handle string URLs
  if (typeof to === "string") {
    return injectTenantIdIntoStringUrl(to, tenantId, excludedRoutes);
  }

  // Handle object URLs
  return injectTenantIdIntoObjectUrl(to, tenantId, excludedRoutes);
};

/**
 * Type for the function returned by useTenantAwareNavigationLinkProps
 */
export type TenantAwareLinkPropsGetter = (to: To, options?: TenantAwareNavigateOptions) => LinkProps;

const useTenantAwareNavigationLinkProps = (
  props?: UseTenantAwareNavigationLinkPropsConfig
): TenantAwareLinkPropsGetter => {
  const { tenantId } = useAppSelector(userState);

  const { excludedRoutes = DEFAULT_EXCLUDED_ROUTES, debug = logger.shouldLog() } = props ?? {};

  return useCallback(
    (to: To, options?: TenantAwareNavigateOptions): LinkProps => {
      // Check if tenant injection should be skipped
      const skipInjection = options?.skipTenantId === true;

      if (skipInjection) {
        if (debug) {
          logger.log("Skipping tenant injection (skipTenantId=true):", {
            context: "TenantAwareNavigation",
            data: {
              to,
            },
          });
        }
        return {
          to,
          ...options,
        };
      }

      // Inject tenantId into URL
      const enhancedTo = injectTenantIdIntoUrl(to, tenantId, excludedRoutes);

      if (debug) {
        logger.log("Injecting tenant:", {
          context: "TenantAwareNavigation",
          data: {
            original: to,
            enhanced: enhancedTo,
            tenantId,
          },
        });
      }

      // Remove skipTenantId from options before passing to navigate
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { skipTenantId, ...restOptions } = options || {};

      return {
        to: enhancedTo,
        ...restOptions,
      };
    },
    [tenantId, excludedRoutes, debug]
  ) as TenantAwareLinkPropsGetter;
};

/**
 * Helper function to convert LinkProps.to to a string URL
 * Useful when you need a string representation of the URL (e.g., for CinRouteTabs)
 *
 * @param to - The To value from LinkProps
 * @returns String representation of the URL
 */
export const toStringUrl = (to: To): string => {
  if (typeof to === "string") {
    return to;
  }

  const pathname = to.pathname || "";
  const search = to.search || "";
  const hash = to.hash || "";

  return `${pathname}${search}${hash}`;
};

export default useTenantAwareNavigationLinkProps;
